import { CheckCircleIcon } from "@heroicons/react/20/solid";
import InfoTooltip from "./InfoTooltip";

/**
 * A single-select group rendered as full-width selectable cards, registered
 * with React Hook Form. Mirrors the prop shape of RadioGroup so it is a near
 * drop-in, but gives a higher-affordance control for primary structural
 * choices (e.g. a service's delivery type).
 *
 * Each card wraps a real, visually-hidden <input type="radio"> so native
 * keyboard navigation (arrow keys), form participation, and RHF `register`
 * all work for free. Selected and focus states are driven purely by Tailwind
 * `peer-*` utilities — no JS/`watch` needed for the visual state.
 *
 * Styling uses design tokens only (cream-*, ink-*, accent-*, shadow-*,
 * rounded-card) per the Quiet Editorial system.
 *
 * @param {string} label - fieldset legend
 * @param {string} [description] - optional helper under the legend
 * @param {string} name - RHF field name
 * @param {Array<{id: string, title: string, helper?: string, icon?: React.ComponentType, tooltip?: string}>} options
 * @param {function} register - RHF register function
 * @param {string|object} [required] - passed to register(name, { required })
 * @param {boolean} [optional=false] - shows "(optional)" in the legend
 */
const SelectableCardGroup = ({
  label,
  description,
  name,
  options,
  register,
  required,
  optional = false,
}) => {
  return (
    <fieldset>
      <legend className="text-sm/6 font-medium text-ink-700">
        {label}
        {optional && (
          <span className="ml-1 font-normal text-ink-400">(optional)</span>
        )}
      </legend>
      {description && (
        <p className="mt-1 text-sm/6 text-ink-500">{description}</p>
      )}
      <div className="mt-4 space-y-3">
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <label
              key={option.id}
              htmlFor={`${name}-${option.id}`}
              className="group relative block cursor-pointer"
            >
              <input
                {...register(name, { required })}
                id={`${name}-${option.id}`}
                value={option.id}
                type="radio"
                className="peer sr-only"
              />
              <div
                className="flex items-start gap-3 rounded-card border border-ink-200 bg-white p-4 shadow-hairline transition-colors hover:border-ink-300 hover:bg-cream-50 peer-checked:border-accent-500 peer-checked:bg-cream-100 peer-checked:shadow-lift peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-500 forced-colors:peer-checked:border-[Highlight] peer-checked:[&_.scg-icon]:text-accent-500 peer-checked:[&_.scg-title]:text-accent-700 peer-checked:[&_.scg-check]:opacity-100"
              >
                {Icon && (
                  <Icon
                    aria-hidden="true"
                    className="scg-icon mt-0.5 size-5 shrink-0 text-ink-400 transition-colors"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="scg-title text-sm/6 font-medium text-ink-900 transition-colors">
                      {option.title}
                    </span>
                    {option.tooltip && (
                      // The card is a <label> — clicking the info icon would
                      // otherwise toggle the radio. Guard the click so the icon
                      // only opens its bubble (hover/focus already work).
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <InfoTooltip
                          content={option.tooltip}
                          label={option.title}
                        />
                      </span>
                    )}
                  </div>
                  {option.helper && (
                    <div className="mt-0.5 text-sm text-ink-500">
                      {option.helper}
                    </div>
                  )}
                </div>
                <CheckCircleIcon
                  aria-hidden="true"
                  className="scg-check size-5 shrink-0 text-accent-500 opacity-0 transition-opacity"
                />
              </div>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default SelectableCardGroup;
